"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button, Alert } from "reactstrap"
import { toast } from "react-toastify"
import { StaffCarService } from "../../../../Service/StaffCarService"
import type { Staff, TokenData } from "./types"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: Staff | null
  token: string
  tokenData: TokenData
}

const StaffModal: React.FC<Props> = ({ isOpen, toggle, onSuccess, initialData, token, tokenData }) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    photo_path: null as File | null,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEditing = !!initialData

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          first_name: initialData.first_name,
          last_name: initialData.last_name,
          phone: initialData.phone,
          photo_path: null,
        })
      } else {
        setFormData({
          first_name: "",
          last_name: "",
          phone: "",
          photo_path: null,
        })
      }
      setError(null)
    }
  }, [isOpen, initialData])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target
    if (name === "photo_path" && files) {
      setFormData((prev) => ({ ...prev, photo_path: files[0] }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const submitData = new FormData()
      submitData.append("first_name", formData.first_name)
      submitData.append("last_name", formData.last_name)
      submitData.append("phone", formData.phone)

      if (formData.photo_path) {
        submitData.append("photo_path", formData.photo_path)
      }

      if (isEditing && initialData) {
        await StaffCarService.updateStaff(token, initialData.id, submitData)
        toast.success("Staff member updated successfully")
      } else {
        await StaffCarService.createStaff(token, submitData)
        toast.success("Staff member created successfully")
      }

      onSuccess()
    } catch (error: any) {
      console.error("Failed to save staff:", error)
      const errorMessage = error.response?.data?.message || error.message || "Failed to save staff member"
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>{isEditing ? "Edit Staff Member" : "Add Staff Member"}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <FormGroup>
            <Label for="first_name">First Name *</Label>
            <Input
              type="text"
              name="first_name"
              id="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter first name"
            />
          </FormGroup>

          <FormGroup>
            <Label for="last_name">Last Name *</Label>
            <Input
              type="text"
              name="last_name"
              id="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter last name"
            />
          </FormGroup>

          <FormGroup>
            <Label for="phone">Phone Number *</Label>
            <Input
              type="tel"
              name="phone"
              id="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter phone number"
            />
          </FormGroup>

          <FormGroup>
            <Label for="photo">Photo</Label>
            <Input
              type="file"
              name="photo_path"
              id="photo_path"
              onChange={handleInputChange}
              disabled={loading}
              accept="image/*"
            />
            <small className="form-text text-muted">Upload a photo for the staff member (optional)</small>
          </FormGroup>

          {initialData?.photo_path && (
            <FormGroup>
              <Label>Current Photo</Label>
              <div>
                <img
                  src={initialData.photo_path || "/placeholder.svg"}
                  alt="Current staff photo"
                  className="img-thumbnail"
                  style={{ maxWidth: "100px", maxHeight: "100px" }}
                />
              </div>
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {isEditing ? "Updating..." : "Creating..."}
              </>
            ) : isEditing ? (
              "Update Staff"
            ) : (
              "Add Staff"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default StaffModal
